import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Sparkles, Loader2 } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { API_URL } from '../lib/apiBase';

/**
 * Loads a curated sample dataset (13 signals) into the active workspace.
 * Variants:
 *   - 'default'  : primary button (used on empty states)
 *   - 'outline'  : secondary button (used in controls / settings)
 *   - 'compact'  : small inline version
 */
const SampleDataButton = ({
  variant = 'default',
  size = 'default',
  onSuccess,
  className = '',
  label = 'Try sample data',
}) => {
  const { currentWorkspaceId, refreshUsage } = useWorkspace();
  const [loading, setLoading] = useState(false);

  const handleLoad = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/sample-data/load`, {
        workspace_id: currentWorkspaceId,
      });

      const { lane_counts, signals_imported } = response.data;
      toast.success(`Loaded ${signals_imported} sample signals`, {
        description: `${lane_counts.build_next} Build · ${lane_counts.fix_now} Fix · ${lane_counts.ignore} Ignore`,
      });

      refreshUsage?.();
      onSuccess?.(response.data);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to load sample data';
      toast.error(typeof msg === 'string' ? msg : 'Failed to load sample data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handleLoad}
      disabled={loading}
      variant={variant === 'default' ? 'default' : 'outline'}
      size={size}
      className={`${variant === 'default' ? 'btn-cta' : ''} ${className}`}
      data-testid="sample-data-button"
    >
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Loading sample…
        </>
      ) : (
        <>
          <Sparkles className="h-4 w-4 mr-2" />
          {label}
        </>
      )}
    </Button>
  );
};

export default SampleDataButton;
